import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Upload, 
  Form, 
  Input, 
  Popconfirm, 
  Tag, 
  Typography,
  App,
  Progress,
  Select,
  Radio,
  Spin,
  Empty,
  Pagination
} from 'antd';
import { 
  UploadOutlined, 
  DeleteOutlined, 
  PlayCircleOutlined, 
  EyeOutlined, 
  SyncOutlined, 
  InboxOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudDownloadOutlined,
  SearchOutlined,
  DownOutlined,
  CloudUploadOutlined,
  DesktopOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { 
  getVideoAuditTasks, 
  uploadVideo, 
  startAudit, 
  retryAudit, 
  deleteVideoAuditTask,
  checkTaskStatus,
  updateTaskAuditStatus,
  updateTaskDuration,
  updateUserVideoDuration,
  getVideoAuditTaskDetail,
  getVideoAuditTaskResult,
  getAiApiConfig,
  refreshMediaUrl,
  createAuditTaskFromDownload
} from '../../services/videoAuditService';
import { getDownloadTasks, getSignedDownloadUrl } from '../../services/plasoService';
import axiosInstance from '../../services/axiosInstance';
import AuditOptionsForm from './AuditOptionsForm';
import { formatDuration } from '../../utils/formatUtils';

const { Dragger } = Upload;
const { Text } = Typography;

const TaskList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [uploadForm] = Form.useForm();
  const [auditForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadedSize, setUploadedSize] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('准备中');
  const [isUploading, setIsUploading] = useState(false);
  const uploadStartTime = useRef(null);
  const lastProgressUpdate = useRef(null);
  const lastUploadedSize = useRef(0);
  const speedSamples = useRef([]);

  // 选择已存档课堂相关状态
  const [selectDownloadModalVisible, setSelectDownloadModalVisible] = useState(false);
  const [downloadTasks, setDownloadTasks] = useState([]);
  const [downloadPagination, setDownloadPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedDownloadTask, setSelectedDownloadTask] = useState(null);
  const [downloadFilters, setDownloadFilters] = useState({
    grade: undefined,
    classType: undefined,
    course: undefined,
    searchText: '',
    custom_tag: undefined,
  });
  const [downloadForm] = Form.useForm();
  
  // 标签相关状态
  const [tagOptions, setTagOptions] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);

  // 届次选项
  const gradeOptions = [
    { value: '2025届', label: '2025届' },
    { value: '2026届', label: '2026届' },
    { value: '2027届', label: '2027届' },
    { value: '2028届', label: '2028届' },
    { value: '测试届次', label: '测试届次' },
  ];
  
  // 班次选项
  const classOptions = [
    { value: '全程班', label: '全程班' },
    { value: '暑假班', label: '暑假班' },
    { value: '寒假班', label: '寒假班' },
    { value: '点题班', label: '点题班' },
    { value: '全日制班', label: '全日制班' },
    { value: '测试班次', label: '测试班次' },
  ];
  
  // 课程选项
  const courseOptions = [
    { value: '高等数学', label: '高等数学' },
    { value: '大学语文', label: '大学语文' },
    { value: '英语四级等级考试', label: '英语四级等级考试' },
    { value: '日语四级等级考试', label: '日语四级等级考试' },
    { value: '计算机一级等级考试', label: '计算机一级等级考试' },
    { value: '管理类', label: '管理类' },
    { value: '财经类', label: '财经类' },
    { value: '电子信息类', label: '电子信息类' },
    { value: '化工生物类', label: '化工生物类' },
    { value: '机械工程类', label: '机械工程类' },
    { value: '土木建筑类', label: '土木建筑类' },
    { value: '计算机类', label: '计算机类' },
    { value: '美术设计类', label: '美术设计类' },
    { value: '环境资源类', label: '环境资源类' },
    { value: '教育类', label: '教育类' },
    { value: '农林类', label: '农林类' },
    { value: '英语类', label: '英语类' },
    { value: '新闻传播类', label: '新闻传播类' },
    { value: '音乐类', label: '音乐类' },
    { value: '日语类', label: '日语类' },
    { value: '法学类', label: '法学类' },
    { value: '食品类', label: '食品类' },
    { value: '文史类', label: '文史类' },
    { value: '医护类', label: '医护类' },
    { value: '测试课程', label: '测试课程' },
  ];

  const { Title } = Typography;
  const navigate = useNavigate();
  const { message } = App.useApp();

  // 获取所有标签
  const fetchTagOptions = async () => {
    setTagLoading(true);
    try {
      const response = await axiosInstance.get('plaso/custom_tags');
      
      if (response && response.code === 0) {
        const tagsData = response.data;
        
        if (tagsData && Array.isArray(tagsData.items)) {
          const options = tagsData.items.map(tag => ({ value: tag.tag_name, label: tag.tag_name }));
          setTagOptions(options);
        } 
        else if (Array.isArray(tagsData)) {
          const options = tagsData.map(tag => ({ value: tag.tag_name, label: tag.tag_name }));
          setTagOptions(options);
        } else {
          setTagOptions([]);
        }
      } else {
        setTagOptions([]);
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
      setTagOptions([]);
    } finally {
      setTagLoading(false);
    }
  };

  // 获取任务列表
  const fetchTasks = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getVideoAuditTasks(page, pageSize);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.items && Array.isArray(responseData.items)) {
        const { items, total } = responseData;
        
        // 处理每个任务的转写数据URL
        const processedItems = items.map(item => {
          if (item.result_data) {
            try {
              const resultData = JSON.parse(item.result_data);
              if (resultData.Result && resultData.Result.Transcription) {
                // 如果Transcription是字符串格式，直接使用
                if (typeof resultData.Result.Transcription === 'string') {
                  item.transcription_url = resultData.Result.Transcription;
                }
                // 如果是对象格式，尝试获取其中的URL字段
                else if (typeof resultData.Result.Transcription === 'object' && resultData.Result.Transcription.Url) {
                  item.transcription_url = resultData.Result.Transcription.Url;
                }
                else {
                  console.error('[API结果] 无法解析Transcription字段:', resultData.Result.Transcription);
                }
              }
            } catch (e) {
              console.error('[时长更新日志] 解析result_data失败:', e);
            }
          }
          return item;
        });

        setData(processedItems);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total,
        });
      } else {
        console.error('获取任务列表失败:', responseData);
        message.error('获取任务列表失败');
      }
    } catch (error) {
      console.error('获取任务列表出错:', error);
      message.error('获取任务列表失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 获取已存档课程列表
  const fetchDownloadTasks = async (page = 1, pageSize = 10) => {
    setDownloadLoading(true);
    try {
      const response = await getDownloadTasks(page, pageSize, downloadFilters);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.items && Array.isArray(responseData.items)) {
        const { items, total } = responseData;
        
        // 处理存档任务列表，根据audit_status字段设置状态和禁用状态
        const processedItems = items.map(item => {
          // 根据audit_status字段设置状态
          if (item.audit_status) {
            // 如果audit_status存在，则根据其值设置状态
            item.disabled = true; // 已加入审核任务，设置为不可选
          } else {
            // 如果audit_status不存在，则设置为可选
            item.disabled = false;
          }
          
          return item;
        });
        
        // 只保留下载状态为已完成的任务
        const completedItems = processedItems.filter(item => item.status === 99);
        
        setDownloadTasks(completedItems);
        setDownloadPagination({
          ...downloadPagination,
          current: page,
          pageSize,
          total,
        });
      } else {
        console.error('获取已存档课程列表失败:', responseData);
        message.error('获取已存档课程列表失败');
      }
    } catch (error) {
      console.error('获取已存档课程列表失败:', error);
      message.error('获取已存档课程列表失败：' + (error.message || '未知错误'));
    } finally {
      setDownloadLoading(false);
    }
  };

  // 获取所有本地网课审核任务（分页获取并合并）
  const getLocalVideoAuditTasks = async () => {
    let allTasks = [];
    let page = 1;
    const pageSize = 100; // 最大允许值
    let hasMore = true;
    
    try {
      while (hasMore) {
        const response = await getVideoAuditTasks(page, pageSize);
        
        if (response && response.data) {
          const { items, total } = response.data;
          
          // 过滤出存档任务
          const localTasks = items.filter(task => 
            task.source_type === 'local' && task.download_task_id
          );
          
          allTasks = [...allTasks, ...localTasks];
          
          // 检查是否还有更多数据
          if (items.length < pageSize || allTasks.length >= total) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }
      return allTasks;
    } catch (error) {
      console.error('获取本地网课审核任务失败:', error);
      return [];
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchTasks(pagination.current, pagination.pageSize);
  }, []);

  // 表格变化处理
  const handleTableChange = (pagination) => {
    fetchTasks(pagination.current, pagination.pageSize);
  };

  // 已存档课程表格变化处理
  const handleDownloadTableChange = (pagination) => {
    fetchDownloadTasks(pagination.current, pagination.pageSize);
  };

  // 显示上传对话框
  const showUploadModal = () => {
    setUploadModalVisible(true);
    setFileList([]);
    uploadForm.resetFields();
  };

  // 显示选择已存档课堂对话框
  const showSelectDownloadModal = () => {
    setSelectDownloadModalVisible(true);
    setSelectedDownloadTask(null);
    downloadForm.resetFields();
    fetchTagOptions(); // 获取标签选项
    fetchDownloadTasks(1, downloadPagination.pageSize);
  };

  // 显示审核选项对话框
  const showAuditOptionsModal = (taskId) => {
    setSelectedTaskId(taskId);
    setAuditModalVisible(true);
    auditForm.resetFields();
  };

  // 处理文件上传前的验证
  const beforeUpload = (file) => {
    // 检查文件类型
    const isVideoOrAudio = file.type.startsWith('video/') || file.type.startsWith('audio/');
    if (!isVideoOrAudio) {
      message.error('请上传视频或音频文件!');
      return Upload.LIST_IGNORE;
    }
    
    // 检查文件大小，限制为5GB
    const isLt5G = file.size / 1024 / 1024 / 1024 < 5;
    if (!isLt5G) {
      message.error('文件大小不能超过5GB!');
      return Upload.LIST_IGNORE;
    }
    
    return false;  // 阻止自动上传
  };

  // 处理上传文件列表改变
  const handleUploadChange = ({ fileList }) => {
    setFileList(fileList);
  };

  // 处理已存档课程筛选条件变化
  const handleDownloadFilterChange = (changedValues) => {
    setDownloadFilters(prev => ({
      ...prev,
      ...changedValues
    }));
    setDownloadPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  // 处理已存档课程搜索
  const handleDownloadSearch = (value) => {
    setDownloadFilters(prev => ({
      ...prev,
      searchText: value
    }));
    setDownloadPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  // 重置已存档课程筛选
  const handleDownloadReset = () => {
    setDownloadFilters({
      grade: undefined,
      classType: undefined,
      course: undefined,
      searchText: '',
      custom_tag: undefined,
    });
    setDownloadPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  // 监听已存档课程筛选条件变化
  useEffect(() => {
    if (selectDownloadModalVisible) {
      fetchDownloadTasks(downloadPagination.current, downloadPagination.pageSize);
    }
  }, [downloadFilters, downloadPagination.current, downloadPagination.pageSize, selectDownloadModalVisible]);

  // 处理已存档课程选择
  const handleDownloadTaskSelect = (task) => {
    // 如果任务已有关联的审核任务，则不允许选择
    if (task.disabled) {
      return;
    }
    
    setSelectedDownloadTask(task);
    downloadForm.setFieldsValue({
      task_name: task.short_desc,
    });
  };

  // 处理选择已存档课堂提交
  const handleSelectDownloadSubmit = async () => {
    try {
      const values = await downloadForm.validateFields();
      if (!selectedDownloadTask) {
        message.warning('请选择已存档课堂');
        return;
      }

      setLoading(true);
      // 获取已存档课程的签名URL
      const response = await getSignedDownloadUrl(selectedDownloadTask.id);
      
      if (response.data.signed_url) {
        // 使用签名URL创建审核任务
        const auditResponse = await createAuditTaskFromDownload({
          task_name: values.task_name,
          file_url: response.data.signed_url,
          file_name: selectedDownloadTask.short_desc,
          file_type: 'video/mp4',  // 固定为video/mp4
          download_task_id: selectedDownloadTask.id  // 添加存档任务ID
        });

        // 检查响应是否包含任务ID，如果有则说明创建成功
        if (auditResponse && auditResponse.data && auditResponse.data.id) {
          message.success('创建审核任务成功');
          setSelectDownloadModalVisible(false);
          setSelectedDownloadTask(null);
          downloadForm.resetFields();
          fetchTasks();
        } else {
          console.error('创建审核任务失败:', auditResponse);
          message.error('创建审核任务失败');
        }
      } else {
        console.error('获取签名URL失败:', {
          data: response.data
        });
        message.error('获取文件签名URL失败');
      }
    } catch (error) {
      console.error('处理已存档课堂提交时出错:', {
        error: error,
        response: error.response,
        message: error.message,
        stack: error.stack
      });
      message.error('创建审核任务失败');
    } finally {
      setLoading(false);
    }
  };

  // 计算平均上传速度
  const calculateAverageSpeed = (loaded, timestamp) => {
    const now = timestamp || Date.now();
    const MAX_SPEED = 200 * 1024 * 1024; // 最大速度限制为200MB/s
    const SAMPLE_WINDOW = 3000; // 3秒的采样窗口
    const MAX_SAMPLES = 10; // 最多保留10个样本
    
    if (!lastProgressUpdate.current || !lastUploadedSize.current) {
      lastProgressUpdate.current = now;
      lastUploadedSize.current = loaded;
      return 0;
    }

    const timeDiff = now - lastProgressUpdate.current;
    // 如果时间间隔太小，使用上次的速度
    if (timeDiff < 500) {
      return uploadSpeed;
    }

    const sizeDiff = loaded - lastUploadedSize.current;
    let instantSpeed = (sizeDiff / timeDiff) * 1000; // 转换为字节/秒

    // 限制最大速度
    instantSpeed = Math.min(instantSpeed, MAX_SPEED);
    
    // 如果速度异常，使用上次的速度
    if (instantSpeed < 0 || !isFinite(instantSpeed)) {
      return uploadSpeed;
    }

    // 添加新的速度样本
    speedSamples.current.push({
      speed: instantSpeed,
      timestamp: now
    });

    // 只保留采样窗口内的样本
    speedSamples.current = speedSamples.current.filter(sample => 
      (now - sample.timestamp) <= SAMPLE_WINDOW
    );

    // 限制样本数量
    if (speedSamples.current.length > MAX_SAMPLES) {
      speedSamples.current = speedSamples.current.slice(-MAX_SAMPLES);
    }

    // 计算加权平均速度，最近的样本权重更大
    const totalWeight = speedSamples.current.length * (speedSamples.current.length + 1) / 2;
    const weightedSpeed = speedSamples.current.reduce((acc, sample, index) => {
      const weight = index + 1;
      return acc + (sample.speed * weight);
    }, 0) / totalWeight;

    // 更新参考值
    lastProgressUpdate.current = now;
    lastUploadedSize.current = loaded;

    return weightedSpeed;
  };

  // 格式化上传阶段显示
  const getUploadPhaseText = (progress, speed) => {
    if (progress === 0) return '准备上传';
    if (progress === 100) return '处理完成';
    if (progress >= 99.5) return '转存至OSS服务器中';
    if (speed === 0 && progress > 0) return '等待响应';
    return '上传中';
  };

  // 格式化剩余时间
  const formatRemainingTime = (loaded, total, speed) => {
    if (speed === 0) return '计算中...';
    const remaining = (total - loaded) / speed;
    if (remaining < 0 || !isFinite(remaining)) return '计算中...';
    
    if (remaining < 60) return `约 ${Math.ceil(remaining)} 秒`;
    if (remaining < 3600) return `约 ${Math.ceil(remaining / 60)} 分钟`;
    return `约 ${Math.round(remaining / 3600 * 10) / 10} 小时`;
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // 提交上传表单
  const handleUploadSubmit = async () => {
    try {
      const values = await uploadForm.validateFields();
      
      if (fileList.length === 0) {
        message.error('请选择要上传的视频文件');
        return;
      }
      
      const file = fileList[0].originFileObj;
      setTotalSize(file.size);
      setUploadedSize(0);
      setUploadProgress(0);
      setUploadSpeed(0);
      setUploadPhase('准备中');
      uploadStartTime.current = Date.now();
      lastProgressUpdate.current = null;
      lastUploadedSize.current = 0;
      speedSamples.current = [];
      setIsUploading(true);

      const response = await uploadVideo(
        values.task_name, 
        file,
        (progressEvent) => {
          const loaded = progressEvent.loaded;
          const total = progressEvent.total;
          
          // 计算精确进度
          const exactProgress = (loaded / total) * 100;
          const displayProgress = exactProgress >= 99.5 ? 99.5 : exactProgress;
          
          // 计算平均上传速度
          const speed = calculateAverageSpeed(loaded, Date.now());
          
          // 更新状态
          setUploadedSize(loaded);
          setUploadProgress(displayProgress);
          setUploadSpeed(speed);
          setUploadPhase(getUploadPhaseText(displayProgress, speed));
        }
      );

      // 检查响应数据，如果包含id和task_name则说明创建成功
      if (response && response.id && response.task_name) {
        setUploadProgress(100);
        setUploadPhase('处理完成');
        message.success('视频上传成功');
        setUploadModalVisible(false);
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        console.error('上传视频失败:', response);
        message.error('视频上传失败');
      }
    } catch (error) {
      console.error('上传视频出错:', error);
      message.error('视频上传失败：' + (error.message || '未知错误'));
    } finally {
      setIsUploading(false);
    }
  };

  // 提交审核选项表单
  const handleAuditOptionsSubmit = async () => {
    try {
      const values = await auditForm.validateFields();
      
      setLoading(true);
      
      const response = await startAudit(selectedTaskId, values);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.status !== 'undefined') {
        message.success('审核任务已启动');
        setAuditModalVisible(false);
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        console.error('启动审核失败:', responseData);
        message.error('启动审核失败');
      }
    } catch (error) {
      console.error('启动审核出错:', error);
      message.error('启动审核失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 重试审核任务
  const handleRetry = async (taskId) => {
    try {
      setLoading(true);
      
      const response = await retryAudit(taskId);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.status !== 'undefined') {
        message.success('审核任务已重新启动');
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        console.error('重试审核失败:', responseData);
        message.error('重试审核失败');
      }
    } catch (error) {
      console.error('重试审核出错:', error);
      message.error('重试审核失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 删除审核任务
  const handleDelete = async (taskId) => {
    try {
      setLoading(true);
      
      const response = await deleteVideoAuditTask(taskId);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.code === 0) {
        message.success(responseData.message || '删除成功');
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        console.error('删除任务失败:', responseData);
        message.error(responseData?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除任务出错:', error);
      message.error('删除失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 查看任务详情
  const handleView = (taskId) => {
    navigate(`/video-audit/detail/${taskId}`);
  };

  // 刷新任务状态
  const handleRefreshStatus = async (taskId) => {
    try {
      setLoading(true);
      
      const response = await checkTaskStatus(taskId);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.status !== 'undefined') {
        message.success('任务状态已刷新');
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        console.error('刷新任务状态失败:', responseData);
        message.error('刷新任务状态失败');
      }
    } catch (error) {
      console.error('刷新任务状态出错:', error);
      message.error('刷新任务状态失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 处理审核状态更新
  const handleUpdateAuditStatus = async (taskId, isReviewed) => {
    try {
      setLoading(true);
      
      const response = await updateTaskAuditStatus(taskId, isReviewed);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.is_reviewed !== 'undefined') {
        message.success(isReviewed ? '已标记为已审核' : '已撤销审核标记');
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        console.error('更新审核状态失败:', responseData);
        message.error('更新审核状态失败');
      }
    } catch (error) {
      console.error('更新审核状态出错:', error);
      message.error('更新审核状态失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 获取状态标签颜色和文本
  const getStatusTag = (status) => {
    const statusMap = {
      0: { color: 'default', text: '未开始' },
      1: { color: 'processing', text: '处理中' },
      2: { color: 'success', text: '处理成功' },
      3: { color: 'error', text: '处理失败' },
    };
    
    const statusInfo = statusMap[status] || { color: 'default', text: '未知' };
    
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      ellipsis: true,
      render: (task_name, record) => (
        <Space>
          {record.source_type === 'local' ? (
            <DesktopOutlined style={{ color: '#1890ff' }} title="存档任务" />
          ) : (
            <CloudUploadOutlined style={{ color: '#52c41a' }} title="上传任务" />
          )}
          {task_name}
        </Space>
      ),
    },
    {
      title: '文件名',
      dataIndex: 'file_name',
      key: 'file_name',
      ellipsis: true,
    },
    {
      title: '文件格式',
      dataIndex: 'file_type',
      key: 'file_type',
      width: 100,
      render: (file_type) => file_type || '-',
    },
    {
      title: '创建人',
      dataIndex: 'creator_username',
      key: 'creator_username',
      width: 100,
      render: (creator_username) => creator_username || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: '审核状态',
      dataIndex: 'is_reviewed',
      key: 'is_reviewed',
      width: 100,
      render: (is_reviewed) => (
        <Tag color={is_reviewed ? 'success' : 'default'}>
          {is_reviewed ? '已审核' : '未审核'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: (create_time) => new Date(create_time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          {record.status === 0 && (
            <Button 
              type="link" 
              size="small" 
              icon={<PlayCircleOutlined />} 
              onClick={() => showAuditOptionsModal(record.id)}
            >
              开始审核
            </Button>
          )}
          
          {record.status === 1 && (
            <Button 
              type="link" 
              size="small" 
              icon={<ReloadOutlined />}
              onClick={() => handleRefreshStatus(record.id)}
              loading={loading && selectedTaskId === record.id}
            >
              刷新状态
            </Button>
          )}
          
          {record.status === 2 && (
            <>
              <Button 
                type="link" 
                size="small" 
                icon={<EyeOutlined />} 
                onClick={() => handleView(record.id)}
              >
                查看详情
              </Button>
              
              {record.is_reviewed ? (
                <Button 
                  type="link" 
                  size="small" 
                  danger
                  icon={<CloseCircleOutlined />} 
                  onClick={() => handleUpdateAuditStatus(record.id, false)}
                >
                  撤销审核
                </Button>
              ) : (
                <Button 
                  type="link" 
                  size="small" 
                  style={{ color: '#52c41a' }}
                  icon={<CheckCircleOutlined />} 
                  onClick={() => handleUpdateAuditStatus(record.id, true)}
                >
                  确认审核
                </Button>
              )}
            </>
          )}
          
          {record.status === 3 && (
            <Button 
              type="link" 
              size="small" 
              icon={<SyncOutlined />} 
              onClick={() => handleRetry(record.id)}
            >
              重试
            </Button>
          )}
          
          <Popconfirm
            title={record.source_type === 'local' ? 
              <div>
                删除此审核任务不会删除云端视频文件。
                <br />
                如需删除视频文件，请前往网课存档页面操作。
              </div> : 
              <div>
                删除此审核任务将同时删除云端视频文件，
                <br />
                是否确认？
              </div>
            }
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              size="small" 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="task-list-container">
      <Card>
        <div className="task-list-header">
          <Title level={4}>网课审核任务列表</Title>
          <Space>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              上传视频
            </Button>
            <Button
              icon={<CloudDownloadOutlined />}
              onClick={showSelectDownloadModal}
            >
              选择已存档课堂
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchTasks()}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Tag color="blue" icon={<ReloadOutlined />}>提示：处理中的任务需要手动点击"刷新状态"按钮获取最新进度；视频提取结果默认存档1个月，超期失效，请及时处理！</Tag>
        </div>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>

      {/* 上传对话框 */}
      <Modal
        title="上传文件"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setUploadModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isUploading}
            onClick={handleUploadSubmit}
          >
            上传
          </Button>
        ]}
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="task_name"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="请输入任务名称" />
          </Form.Item>
          
          <Form.Item label="视频文件">
            <Dragger
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              showUploadList={{ showRemoveIcon: true }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">支持单个视频或音频文件上传，大小不超过5GB</p>
            </Dragger>
          </Form.Item>
          
          {isUploading && (
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <Space>
                  <span>{uploadPhase}</span>
                  <span>{formatFileSize(uploadedSize)} / {formatFileSize(totalSize)}</span>
                </Space>
              </div>
              <Progress
                percent={Number(uploadProgress.toFixed(2))}
                status={uploadProgress >= 99.5 && uploadProgress < 100 ? "active" : "normal"}
                strokeColor={uploadProgress >= 99.5 ? "#1677ff" : "#52c41a"}
              />
              <div style={{ marginTop: 8 }}>
                <Space>
                  <span>速度: {formatFileSize(uploadSpeed)}/s</span>
                  <span>预计剩余: {formatRemainingTime(uploadedSize, totalSize, uploadSpeed)}</span>
                </Space>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {/* 审核选项对话框 */}
      <Modal
        title="审核选项"
        open={auditModalVisible}
        onOk={handleAuditOptionsSubmit}
        onCancel={() => setAuditModalVisible(false)}
        confirmLoading={loading}
        width={700}
        maskClosable={false}
      >
        <AuditOptionsForm form={auditForm} />
      </Modal>

      {/* 选择已存档课堂对话框 */}
      <Modal
        title="选择已存档课堂"
        open={selectDownloadModalVisible}
        onCancel={() => {
          setSelectDownloadModalVisible(false);
          setSelectedDownloadTask(null);
          downloadForm.resetFields();
        }}
        width={900}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSelectDownloadModalVisible(false);
              setSelectedDownloadTask(null);
              downloadForm.resetFields();
            }}
          >
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSelectDownloadSubmit}
            loading={loading}
          >
            确定
          </Button>,
        ]}
        maskClosable={false}
      >
        <Form
          form={downloadForm}
          layout="vertical"
        >
          <Form.Item
            name="task_name"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="请输入任务名称" />
          </Form.Item>

          {/* 筛选表单 */}
          <Card 
            title="课程筛选" 
            size="small" 
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <Button 
                  icon={<SearchOutlined />} 
                  onClick={() => fetchDownloadTasks(1, downloadPagination.pageSize)}
                  type="primary"
                  size="small"
                >
                  搜索
                </Button>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={handleDownloadReset}
                  size="small"
                >
                  重置
                </Button>
              </Space>
            }
          >
            <Form
              form={downloadForm}
              layout="inline"
              onValuesChange={(changedValues) => handleDownloadFilterChange(changedValues)}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%' }}
            >
              <Form.Item name="grade" label="届次" style={{ flex: 1, minWidth: '150px' }}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择届次"
                  allowClear
                  options={gradeOptions}
                />
              </Form.Item>
              <Form.Item name="classType" label="班次" style={{ flex: 1, minWidth: '150px' }}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择班次"
                  allowClear
                  options={classOptions}
                />
              </Form.Item>
              <Form.Item name="course" label="课程" style={{ flex: 1, minWidth: '150px' }}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择课程"
                  allowClear
                  options={courseOptions}
                />
              </Form.Item>
              <Form.Item name="custom_tag" label="标签" style={{ flex: 1, minWidth: '150px' }}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择标签"
                  allowClear
                  loading={tagLoading}
                  options={tagOptions}
                />
              </Form.Item>
              <Form.Item name="searchText" label="搜索" style={{ flex: 1, minWidth: '200px' }}>
                <Input 
                  placeholder="输入教师或课程名称" 
                  style={{ width: '100%' }}
                  allowClear
                  onChange={(e) => handleDownloadSearch(e.target.value)}
                />
              </Form.Item>
            </Form>
          </Card>
        </Form>

        <div style={{ height: '400px', overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: '2px', padding: '8px' }}>
          <Radio.Group 
            value={selectedDownloadTask ? selectedDownloadTask.id : undefined}
            onChange={(e) => {
              const selectedTask = downloadTasks.find(task => task.id === e.target.value);
              if (selectedTask && !selectedTask.disabled) {
                handleDownloadTaskSelect(selectedTask);
              }
            }}
            style={{ width: '100%', display: 'block' }}
          >
            {downloadLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="small" />
                <div>加载课程中...</div>
              </div>
            ) : downloadTasks.length === 0 ? (
              <Empty description="没有匹配的已存档课程，请调整筛选条件" />
            ) : (
              <div style={{ width: '100%' }}>
                {downloadTasks.map(task => (
                  <div key={task.id} style={{ 
                    width: '100%', 
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <Radio 
                      value={task.id} 
                      disabled={task.disabled} 
                      style={{ 
                        marginTop: '12px',
                        marginRight: '8px',
                        flexShrink: 0
                      }} 
                    />
                    <div style={{ 
                      flex: 1,
                      padding: '12px', 
                      backgroundColor: task.disabled ? '#f5f5f5' : '#fafafa',
                      borderRadius: '6px',
                      border: 'none',
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      minHeight: '80px',
                      opacity: task.disabled ? 0.6 : 1
                    }}>
                      <div style={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        minWidth: '0', 
                        paddingRight: '16px'
                      }}>
                        {/* 课程名称 */}
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong style={{ 
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '14px'
                          }}>
                            {task.short_desc}
                          </Text>
                          <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {task.source_type === 'local' && (
                              <Tag color="blue" size="small">存档任务</Tag>
                            )}
                            {task.audit_status && (
                              <Tag 
                                size="small"
                                color={
                                  task.audit_status === '已审核' ? 'green' : 
                                  task.audit_status === '审核中' ? 'blue' : 
                                  task.audit_status === '已加入' ? 'orange' : 'default'
                                }
                              >
                                {task.audit_status}
                              </Tag>
                            )}
                          </div>
                        </div>
                        
                        {/* 届次、班次、课程、讲师信息在一行 - 不换行 */}
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginBottom: '4px', 
                          display: 'flex', 
                          gap: '12px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        }}>
                          {task.grade_name && (
                            <span style={{ flexShrink: 0 }}>届次: {task.grade_name}</span>
                          )}
                          {task.class_name && (
                            <span style={{ flexShrink: 0 }}>班次: {task.class_name}</span>
                          )}
                          {task.course_name && (
                            <span style={{ flexShrink: 0 }}>课程: {task.course_name}</span>
                          )}
                          {task.teacher_name && (
                            <span style={{ 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '120px'
                            }}>讲师: {task.teacher_name}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* 右侧信息：创建时间、标签、课程时长 */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'flex-end', 
                        gap: '4px', 
                        width: '250px',
                        flexShrink: 0 
                      }}>
                        {/* 创建时间和标签在同一行 */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          flexWrap: 'wrap', 
                          justifyContent: 'flex-end',
                          width: '100%'
                        }}>
                          {/* 创建时间 */}
                          {task.create_time && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#52c41a', 
                              display: 'flex', 
                              alignItems: 'center',
                              whiteSpace: 'nowrap'
                            }}>
                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                              创建时间: {task.create_time}
                            </div>
                          )}
                          
                          {/* 标签显示 */}
                          {task.custom_tags && task.custom_tags.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {task.custom_tags.map((tag, index) => (
                                <Tag 
                                  key={index}
                                  size="small"
                                  style={{ 
                                    backgroundColor: '#f0f0f0', 
                                    color: '#666',
                                    border: '1px solid #d9d9d9',
                                    fontSize: '10px',
                                    margin: 0
                                  }}
                                >
                                  {tag}
                                </Tag>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* 课程时长 */}
                        {task.duration && task.duration > 0 && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}>
                            <FieldTimeOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                            <Text style={{ fontSize: '12px', color: '#1890ff' }}>
                              {formatDuration(task.duration)}
                            </Text>
                            {task.duration < 600 && ( // 10分钟 = 600秒
                              <Tag 
                                size="small"
                                style={{ 
                                  backgroundColor: '#ff4d4f', 
                                  color: '#fff',
                                  border: 'none',
                                  fontSize: '10px',
                                  margin: 0
                                }}
                              >
                                测试课
                              </Tag>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Radio.Group>
        </div>
        
        {/* 分页组件 */}
        {downloadPagination.total > 0 && (
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Pagination
              current={downloadPagination.current}
              pageSize={downloadPagination.pageSize}
              total={downloadPagination.total}
              onChange={(page, pageSize) => {
                setDownloadPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize
                }));
                fetchDownloadTasks(page, pageSize);
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `共 ${total} 条记录`}
            />
          </div>
        )}
      </Modal>

      <style>{`
        .task-list-container {
          padding: 24px;
        }
        .task-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
      `}</style>
    </div>
  );
};

export default TaskList; 